import api from './api';
import {
  mapResourceNode,
  mapResourceRelationship,
  mapResourceTopologyHistoryItem,
  mapResourceTopologyImportCommitResult,
  mapResourceTopologyImportPreview,
  mapResourceTopologyIssue,
  mapResourceTopologyVersion,
  unwrapTopologyData,
} from './prodResourceTopologyMappers';
import type {
  ResourceNode,
  ResourceNodeDraft,
  ResourceRelationship,
  ResourceRelationshipDraft,
  ResourceTopologyHistoryItem,
  ResourceTopologyImportCommitResult,
  ResourceTopologyImportPayload,
  ResourceTopologyImportPreview,
  ResourceTopologyValidationResult,
  ResourceTopologyVersion,
  ResourceTopologyWorkspace,
  TopologyMutationResult,
} from '../types/prodResourceTopology';

const ROOT = '/prod/resource-topologies';

type RawRecord = Record<string, unknown>;

const asRecord = (value: unknown): RawRecord => (
  value && typeof value === 'object' ? value as RawRecord : {}
);

const unwrapRecord = (payload: unknown): RawRecord => asRecord(unwrapTopologyData(payload));

const mapMutationResult = (payload: unknown): TopologyMutationResult => {
  const raw = unwrapRecord(payload);
  return {
    version: mapResourceTopologyVersion(raw.version),
    resource: raw.node ? mapResourceNode(raw.node) : undefined,
    relationship: raw.relation
      ? mapResourceRelationship(raw.relation)
      : raw.parentRelation ? mapResourceRelationship(raw.parentRelation) : undefined,
  };
};

const mapIssues = (value: unknown) => (
  Array.isArray(value) ? value.map(mapResourceTopologyIssue) : []
);

export { normalizeResourceTopologyError } from './prodResourceTopologyErrors';

export const prodResourceTopologyApi = {
  async listVersions(facilityCode: string): Promise<ResourceTopologyHistoryItem[]> {
    const response = await api.get(ROOT, { params: { facilityCode } });
    const data = unwrapTopologyData(response.data);
    return Array.isArray(data) ? data.map(mapResourceTopologyHistoryItem) : [];
  },

  async createDraft(input: {
    facilityCode: string;
    name?: string;
    baseVersionId?: string;
  }): Promise<ResourceTopologyVersion> {
    const response = await api.post(ROOT, input);
    return mapResourceTopologyVersion(unwrapTopologyData(response.data));
  },

  async getVersion(versionId: string): Promise<ResourceTopologyVersion> {
    const response = await api.get(`${ROOT}/${versionId}`);
    return mapResourceTopologyVersion(unwrapTopologyData(response.data));
  },

  async listNodes(versionId: string): Promise<ResourceNode[]> {
    const response = await api.get(`${ROOT}/${versionId}/nodes`);
    const data = unwrapTopologyData(response.data);
    return Array.isArray(data) ? data.map(mapResourceNode) : [];
  },

  async listRelations(versionId: string): Promise<ResourceRelationship[]> {
    const response = await api.get(`${ROOT}/${versionId}/relations`);
    const data = unwrapTopologyData(response.data);
    return Array.isArray(data) ? data.map(mapResourceRelationship) : [];
  },

  async listValidationIssues(versionId: string) {
    const response = await api.get(`${ROOT}/${versionId}/validation-issues`);
    const data = unwrapTopologyData(response.data);
    return mapIssues(data);
  },

  async getWorkspace(versionId: string): Promise<ResourceTopologyWorkspace> {
    const [version, resources, relationships, issues] = await Promise.all([
      this.getVersion(versionId),
      this.listNodes(versionId),
      this.listRelations(versionId),
      this.listValidationIssues(versionId),
    ]);
    return { version, resources, relationships, issues };
  },

  async createNode(
    versionId: string,
    expectedRevision: number,
    draft: ResourceNodeDraft,
    parentResourceId?: string | null,
  ): Promise<TopologyMutationResult> {
    const response = await api.post(`${ROOT}/${versionId}/nodes`, {
      revision: expectedRevision,
      ...draft,
      parentResourceId: parentResourceId ?? null,
    });
    return mapMutationResult(response.data);
  },

  async replaceParent(
    versionId: string,
    resourceId: string,
    expectedRevision: number,
    parentResourceId: string | null,
  ): Promise<TopologyMutationResult> {
    const response = await api.put(`${ROOT}/${versionId}/nodes/${resourceId}/parent`, {
      revision: expectedRevision,
      parentResourceId,
    });
    return mapMutationResult(response.data);
  },

  async updateNode(
    versionId: string,
    resourceId: string,
    expectedRevision: number,
    patch: Partial<Pick<ResourceNodeDraft, 'name' | 'lifecycleStatus' | 'attributes' | 'note'>> & {
      parentResourceId?: string | null;
    },
  ): Promise<TopologyMutationResult> {
    const response = await api.patch(`${ROOT}/${versionId}/nodes/${resourceId}`, {
      revision: expectedRevision,
      ...patch,
    });
    return mapMutationResult(response.data);
  },

  async retireNode(
    versionId: string,
    resourceId: string,
    expectedRevision: number,
  ): Promise<TopologyMutationResult> {
    const response = await api.post(`${ROOT}/${versionId}/nodes/${resourceId}/retire`, {
      revision: expectedRevision,
    });
    return mapMutationResult(response.data);
  },

  async createRelation(
    versionId: string,
    expectedRevision: number,
    draft: ResourceRelationshipDraft,
  ): Promise<TopologyMutationResult> {
    const response = await api.post(`${ROOT}/${versionId}/relations`, {
      revision: expectedRevision,
      relationType: draft.type,
      sourceResourceId: draft.sourceResourceId,
      targetResourceId: draft.targetResourceId,
      priority: draft.priority,
      note: draft.note,
    });
    return mapMutationResult(response.data);
  },

  async updateRelation(
    versionId: string,
    relationshipId: string,
    expectedRevision: number,
    patch: Pick<ResourceRelationshipDraft, 'priority' | 'note'>,
  ): Promise<TopologyMutationResult> {
    const response = await api.patch(`${ROOT}/${versionId}/relations/${relationshipId}`, {
      revision: expectedRevision,
      priority: patch.priority,
      note: patch.note,
    });
    return mapMutationResult(response.data);
  },

  async deleteRelation(
    versionId: string,
    relationshipId: string,
    expectedRevision: number,
  ): Promise<TopologyMutationResult> {
    const response = await api.delete(`${ROOT}/${versionId}/relations/${relationshipId}`, {
      data: { revision: expectedRevision },
    });
    return mapMutationResult(response.data);
  },

  async validate(
    versionId: string,
    expectedRevision: number,
  ): Promise<ResourceTopologyValidationResult> {
    const response = await api.post(`${ROOT}/${versionId}/validate`, {
      revision: expectedRevision,
    });
    const raw = unwrapRecord(response.data);
    return {
      version: mapResourceTopologyVersion(raw.version),
      issues: mapIssues(raw.issues),
    };
  },

  async publish(versionId: string, expectedRevision: number): Promise<ResourceTopologyVersion> {
    const response = await api.post(`${ROOT}/${versionId}/publish`, {
      revision: expectedRevision,
    });
    return mapResourceTopologyVersion(unwrapTopologyData(response.data));
  },

  async abandon(versionId: string, expectedRevision: number): Promise<ResourceTopologyVersion> {
    const response = await api.post(`${ROOT}/${versionId}/abandon`, {
      revision: expectedRevision,
    });
    return mapResourceTopologyVersion(unwrapTopologyData(response.data));
  },

  async previewImport(
    versionId: string,
    expectedRevision: number,
    payload: ResourceTopologyImportPayload,
  ): Promise<ResourceTopologyImportPreview> {
    const response = await api.post(`${ROOT}/${versionId}/import/preview`, {
      revision: expectedRevision,
      ...payload,
    });
    return mapResourceTopologyImportPreview(response.data);
  },

  async commitImport(
    versionId: string,
    expectedRevision: number,
    previewToken: string,
    payload: ResourceTopologyImportPayload,
  ): Promise<ResourceTopologyImportCommitResult> {
    const response = await api.post(`${ROOT}/${versionId}/import`, {
      revision: expectedRevision,
      previewToken,
      ...payload,
    });
    return mapResourceTopologyImportCommitResult(response.data);
  },
};
